/**
 * Transfer Opportunities — ADI-facing inbox.
 *
 * The ADI sees calm positive cards for every learner match that pinged
 * their Transfer Network filters. Each card exposes one of two actions:
 * "I'm interested" (records intent, flips request to matched) or "Not for
 * me right now" (soft withdraw). No negative language — learners are never
 * "passed" or "rejected".
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Handshake, MapPin, Clock3, Check, X, Settings } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REASON_LABELS = {
  moved_area: "Moved area",
  instructor_unavailable: "Current instructor unavailable",
  better_location: "Wants a closer match",
  different_style: "Wants a different style",
  transmission_change: "Transmission change",
  personal_preference: "Personal preference",
  instructor_initiated: "Instructor suggested transfer",
};

const TYPE_LABELS = {
  beginner: "Starting out",
  nervous: "Builds confidence best with patient teaching",
  test_ready: "Test-ready — preparing for test",
  relocation: "Relocation learner — keeping momentum",
};

function OpportunityCard({ opp, onInterested, onPass }) {
  const reason = REASON_LABELS[opp.reason] || opp.reason;
  const typeHint = TYPE_LABELS[opp.learner_type];
  const myStatus = opp.my_interest_status;

  return (
    <div
      data-testid={`transfer-opportunity-${opp.id}`}
      className="bg-white rounded-2xl border border-slate-200 p-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0">
          <Handshake className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
              {opp.new_postcode_outward || opp.new_postcode}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
              {opp.transmission}
            </span>
            {opp.test_booked && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">
                Test booked
              </span>
            )}
            {opp.match_level === "exact_outward" && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">
                Strong match
              </span>
            )}
          </div>
          <h3 className="text-sm font-bold text-slate-900 mt-1">
            {opp.learner_first_name} is looking for a new match — {opp.new_postcode_outward || opp.new_postcode}
          </h3>
          <p className="text-xs text-slate-600 mt-1">{reason}</p>
          {typeHint && (
            <p className="text-[11px] text-slate-500 mt-1 italic">{typeHint}</p>
          )}
          {opp.availability_note && (
            <div className="flex items-start gap-1 mt-2 text-[11px] text-slate-600">
              <Clock3 className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>{opp.availability_note}</span>
            </div>
          )}
          {opp.teaching_preference && (
            <p className="text-[11px] text-slate-500 italic mt-1">"{opp.teaching_preference}"</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
        {myStatus === "interested" ? (
          <>
            <span
              data-testid={`opp-interested-${opp.id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-lg px-3 py-1.5"
            >
              <Check className="w-3.5 h-3.5" />
              You've expressed interest · {opp.learner_first_name} will see your profile
            </span>
            <button
              type="button"
              onClick={() => onPass(opp)}
              data-testid={`opp-withdraw-${opp.id}`}
              className="ml-auto text-[11px] font-semibold text-slate-500 hover:text-slate-900 px-2 py-1.5"
            >
              Withdraw
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => onInterested(opp)}
              data-testid={`opp-interested-btn-${opp.id}`}
              className="inline-flex items-center gap-1.5 bg-indigo-600 text-white text-xs font-bold rounded-lg px-3 py-2 hover:bg-indigo-700"
            >
              <Check className="w-3.5 h-3.5" />
              I'm interested
            </button>
            <button
              type="button"
              onClick={() => onPass(opp)}
              data-testid={`opp-pass-btn-${opp.id}`}
              className="text-xs font-semibold text-slate-500 hover:text-slate-900 px-3 py-2"
            >
              Not for me right now
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function TransferOpportunities() {
  const navigate = useNavigate();
  const goBack = useSafeBack();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/transfer-network/opportunities`,
        { withCredentials: true },
      );
      setOpportunities(data?.opportunities || []);
      // Mark all notifications as read on open
      const unread = (data?.opportunities || []).filter((o) => !o.notification_read);
      for (const o of unread) {
        if (o.notification_id) {
          axios.post(
            `${API_URL}/api/transfer-network/notifications/${o.notification_id}/read`,
            {},
            { withCredentials: true },
          ).catch(() => {});
        }
      }
    } catch {
      setOpportunities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleInterested = async (opp) => {
    try {
      await axios.post(
        `${API_URL}/api/transfer-network/requests/${opp.id}/interest`,
        {},
        { withCredentials: true },
      );
      toast.success(`Nice — ${opp.learner_first_name} will see your profile`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save your interest");
    }
  };

  const handlePass = async (opp) => {
    try {
      await axios.post(
        `${API_URL}/api/transfer-network/requests/${opp.id}/withdraw`,
        {},
        { withCredentials: true },
      );
      setOpportunities((list) => list.filter((o) => o.id !== opp.id));
    } catch {
      toast.error("Couldn't update");
    }
  };

  return (
    <div data-testid="transfer-opportunities-page" className="min-h-dvh no-overflow-x bg-slate-50">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => goBack()}
            className="p-2 rounded-xl hover:bg-slate-100"
            data-testid="transfer-opps-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-slate-900">Transfer opportunities</h1>
            <p className="text-[11px] text-slate-500">Learners looking for a local match</p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/settings/transfer-network")}
            data-testid="transfer-opps-settings"
            className="p-2 rounded-xl hover:bg-slate-100"
            title="Transfer Network settings"
          >
            <Settings className="w-5 h-5 text-slate-600" />
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        {loading ? (
          <p className="text-sm text-slate-400 text-center py-8">Loading…</p>
        ) : opportunities.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
            <MapPin className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-600">
              No new opportunities right now. We'll let you know when a learner in your
              area is looking for a new match.
            </p>
            <button
              type="button"
              onClick={() => navigate("/settings/transfer-network")}
              data-testid="transfer-opps-empty-settings"
              className="mt-3 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
            >
              Adjust your Transfer Network preferences →
            </button>
          </div>
        ) : (
          opportunities.map((o) => (
            <OpportunityCard
              key={o.id}
              opp={o}
              onInterested={handleInterested}
              onPass={handlePass}
            />
          ))
        )}
      </div>
    </div>
  );
}
